import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Maximize2 } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Lightbox from "@/components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IssueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIssue(null);
    axios
      .get(`${API}/issues/${id}`)
      .then((r) => setIssue(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const openAt = (i) => {
    setLbIndex(i);
    setLbOpen(true);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Nav solid />
        <div className="pt-40 pb-32 text-center px-6">
          <h1 className="font-serif-display text-5xl">Issue not found</h1>
          <Link to="/#issues" className="mt-6 inline-block font-accent uppercase text-xs tracking-[0.25em] border border-black px-7 py-3.5 hover:bg-black hover:text-white transition-colors">
            Back to Issues
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!issue) return <div className="min-h-screen bg-white" />;

  return (
    <div className="bg-white text-[#0a0a0a]" data-testid="issue-detail">
      <Nav solid />
      <section className="relative h-[72vh] md:h-[88vh] bg-black overflow-hidden grain">
        <img src={issue.cover} alt={issue.title} className="absolute inset-0 h-full w-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-black/50" />
        <div className="relative h-full flex flex-col justify-between px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-12 text-white">
          <button
            onClick={() => navigate(-1)}
            data-testid="issue-back"
            className="flex items-center gap-2 font-accent uppercase text-xs tracking-[0.25em] text-white/70 hover:text-white transition-colors w-fit"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <span className="font-accent uppercase text-xs tracking-[0.3em] text-white/70">
              {issue.season} {issue.year} · The Archive
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="font-serif-display text-5xl md:text-8xl leading-[0.88] tracking-tight mt-3"
            >
              {issue.title}
            </motion.h1>
            <p className="mt-5 max-w-lg font-light text-white/80 text-lg leading-relaxed">
              {issue.description}
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight">
            The <span className="italic font-light">Lookbook</span>
          </h2>
          <span className="font-accent uppercase text-xs tracking-[0.25em] text-[#4a4a4a]">
            {issue.gallery.length} Frames · Tap to expand
          </span>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-7 [column-fill:_balance]" data-testid="issue-gallery">
          {issue.gallery.map((src, i) => (
            <motion.button
              key={src + i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              onClick={() => openAt(i)}
              data-testid={`issue-photo-${i}`}
              className="group relative mb-5 md:mb-7 block w-full overflow-hidden bg-[#f5f5f5]"
            >
              <img src={src} alt={`${issue.title} frame ${i + 1}`} className="w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors duration-400">
                <Maximize2 size={28} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </span>
            </motion.button>
          ))}
        </div>
      </section>

      <Lightbox
        images={issue.gallery}
        index={lbIndex}
        open={lbOpen}
        onClose={() => setLbOpen(false)}
        onChange={setLbIndex}
      />
      <Footer />
    </div>
  );
};

export default IssueDetail;
